import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import CustomCheckboxDropdown from '../../../components/CustomCheckboxDropdown/CustomCheckboxDropdown';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { PrimaryButton, WhiteButton } from '../../../styledComponents/buttons';
import {
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';

const AddEmtithalMasterTaskModal = ({
  isOpen,
  toggle,
  publicZoneList,
  getOfficesShortList,
  officesShortList,
  addEmtithalMasterTask,
  isLoading,
  reload,
  setReload,
  editEmtithalMasterTask,
  taskToEdit,
  isEditMode,
  getMasterSubTasks,
  masterSubTasksList,
  masterSubTasksLoading,
}) => {
  const { t } = useTranslation();

  const [taskName, setTaskName] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [selectedMasterSubtasks, setSelectedMasterSubtasks] = useState([]);
  const [masterSubtaskOptions, setMasterSubtaskOptions] = useState([]);
  const [selectAllOffices, setSelectAllOffices] = useState(false);

  useEffect(() => {
    if (isEditMode && taskToEdit) {
      setTaskName(taskToEdit.name || '');

      const subTasks = taskToEdit.sub_tasks || taskToEdit.master_tasks || [];
      if (subTasks.length > 0 && masterSubtaskOptions.length > 0) {
        const selected = subTasks.map(mt => ({
          label: mt.name ? `${mt.code || ''} - ${mt.name}` : mt.code || mt.id,
          value: mt.id,
        }));
        setSelectedMasterSubtasks(selected);
      }

      const zoneId = taskToEdit.public_zone || taskToEdit.public_zone_id;
      if (zoneId && publicZoneOptions.length > 0) {
        const zone = publicZoneOptions.find(z => z.value === zoneId);
        if (zone) setSelectedPublicZone(zone);
      }

      const offices = taskToEdit.related_offices || taskToEdit.office || [];
      if (offices.length > 0 && officeOptions.length > 0) {
        const relatedOffices = offices.map(office => ({
          label: office.name,
          value: office.id,
        }));
        setSelectedOffices(relatedOffices);
      }
    }
  }, [isEditMode, taskToEdit, publicZoneOptions, masterSubtaskOptions, officeOptions]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      const newArr = publicZoneList.map(zone => ({
        label: zone.name,
        value: zone.id,
      }));
      setPublicZoneOptions(newArr);
      if (!isEditMode || !selectedPublicZone) {
        setSelectedPublicZone(newArr[0]);
      }
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone?.value) {
      if (getMasterSubTasks) {
        getMasterSubTasks({
          public_zone_id: selectedPublicZone.value,
        });
      }
      if (getOfficesShortList) {
        getOfficesShortList({
          public_zone_id: selectedPublicZone.value,
        });
      }
    } else if (getOfficesShortList) {
      getOfficesShortList();
    }
  }, [selectedPublicZone]);

  useEffect(() => {
    if (masterSubTasksList && masterSubTasksList.length > 0) {
      const options = masterSubTasksList.map(mt => ({
        label: mt.name ? `${mt.code || ''} - ${mt.name}` : mt.code || mt.id,
        value: mt.id,
      }));
      setMasterSubtaskOptions(options);
    } else {
      setMasterSubtaskOptions([]);
    }
  }, [masterSubTasksList]);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      const list = officesShortList.map(office => ({
        label: office.name,
        value: office.id,
      }));
      setOfficeOptions(list);

      const offices = taskToEdit?.related_offices || taskToEdit?.office || [];
      if (isEditMode && taskToEdit && offices.length > 0) {
        if (offices.length === officesShortList.length) {
          setSelectAllOffices(true);
          setSelectedOffices(list);
        } else {
          setSelectAllOffices(false);
          setSelectedOffices(
            offices.map(office => ({
              label: office.name,
              value: office.id,
            })),
          );
        }
      } else if (!isEditMode) {
        setSelectedOffices([]);
        setSelectAllOffices(false);
      }
    }
  }, [officesShortList, isEditMode, taskToEdit]);

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
    setSelectedOffices([]);
    setSelectAllOffices(false);
    setSelectedMasterSubtasks([]);
  };

  const onOfficeChange = selected => {
    const allOption = selected.find(opt => opt.value === 'all');
    if (allOption) {
      setSelectAllOffices(true);
      setSelectedOffices(officeOptions);
    } else {
      setSelectAllOffices(false);
      setSelectedOffices(selected);
    }
    if (saveErrors?.office) {
      setSaveErrors(prev => ({ ...prev, office: undefined }));
    }
  };

  const onMasterSubtaskChange = selected => {
    setSelectedMasterSubtasks(selected);
    if (saveErrors?.master_subtask) {
      setSaveErrors(prev => ({ ...prev, master_subtask: undefined }));
    }
  };

  const checkError = () => {
    const errors = {};
    let formIsValid = true;

    if (!taskName.trim()) {
      formIsValid = false;
      errors.taskName = t('task_name_required');
    } else if (checkUrl(taskName)) {
      formIsValid = false;
      errors.taskName = t('task_name_cannot_contain_url');
    } else if (checkHtml(taskName)) {
      formIsValid = false;
      errors.taskName = t('invalid_task_name');
    }

    if (!selectedPublicZone) {
      formIsValid = false;
      errors.public_zone = t('public_zone_required');
    }

    if (!selectAllOffices && (!selectedOffices || selectedOffices.length === 0)) {
      formIsValid = false;
      errors.office = t('at_least_one_office_required');
    }

    if (!selectedMasterSubtasks || selectedMasterSubtasks.length === 0) {
      formIsValid = false;
      errors.master_subtask = t('at_least_one_master_subtask_required');
    }

    setSaveErrors(errors);
    return formIsValid;
  };

  const handleSubmit = () => {
    if (!checkError()) return;

    const related_offices_ids = selectAllOffices
      ? officeOptions.map(o => o.value)
      : selectedOffices.map(o => o.value);
    const sub_tasks_ids = selectedMasterSubtasks.map(s => s.value);

    const payload = {
      name: taskName,
      public_zone: selectedPublicZone.value,
      related_offices_ids,
      sub_tasks_ids,
    };

    const callback = () => {
      handleToggle();
      if (setReload) setReload(prev => !prev);
    };

    if (isEditMode && taskToEdit) {
      editEmtithalMasterTask({
        task_id: taskToEdit.id,
        ...payload,
        callback,
      });
    } else {
      addEmtithalMasterTask({ ...payload, callback });
    }
  };

  const clearState = () => {
    setTaskName('');
    setSaveErrors({});
    setSelectedPublicZone('');
    setSelectedOffices([]);
    setSelectedMasterSubtasks([]);
    setSelectAllOffices(false);
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: '2vh', maxWidth: '600px', overflow: 'visible' }}
    >
      <ModalBody style={{ padding: '20px' }}>
        <CommonText name title fontSize="16px" margin="auto 0 20px 0">
          {isEditMode
            ? t('edit_master_emtithal_task')
            : t('add_master_emtithal_task')}
        </CommonText>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('public_zone')} <RedStar>*</RedStar>
          </InputLabel>
          <Select
            isSearchable
            value={selectedPublicZone}
            options={publicZoneOptions}
            placeholder={t('select_zone')}
            onChange={onPublicZoneChange}
            styles={CreateProjectDropdown()}
            filterOption={filterOption}
          />
          {saveErrors?.public_zone && (
            <FormAlert margin="0 0 0 5px">{saveErrors.public_zone}</FormAlert>
          )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('master_subtasks')} <RedStar>*</RedStar>
          </InputLabel>
          <CustomCheckboxDropdown
            options={masterSubtaskOptions}
            selectedOptions={selectedMasterSubtasks}
            onChange={onMasterSubtaskChange}
            placeholder={t('select_master_subtasks')}
            countLabel="master_subtasks_selected"
            error={saveErrors?.master_subtask}
            errorMessage={saveErrors?.master_subtask}
            isLoading={masterSubTasksLoading}
            disabled={!selectedPublicZone || masterSubtaskOptions.length === 0}
          />
          {selectedPublicZone &&
            masterSubtaskOptions.length === 0 &&
            !masterSubTasksLoading && (
              <FormAlert margin="5px 0 0 0">
                {t('no_master_subtasks_available')}
              </FormAlert>
            )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('emtithal_task_name')} <RedStar>*</RedStar>
          </InputLabel>
          <InputField
            type="text"
            placeholder={t('type_task_name')}
            value={taskName}
            onChange={e => {
              setTaskName(e.target.value);
              if (saveErrors?.taskName) {
                setSaveErrors(prev => ({ ...prev, taskName: undefined }));
              }
            }}
            style={
              saveErrors?.taskName && {
                border: '1px solid #fe5969',
                backgroundColor: 'rgba(252, 87, 104, 0.05)',
              }
            }
          />
          {saveErrors?.taskName && (
            <FormAlert>{saveErrors.taskName}</FormAlert>
          )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('office')} <RedStar>*</RedStar>
          </InputLabel>
          <CustomCheckboxDropdown
            options={[
              { label: t('all_offices'), value: 'all' },
              ...officeOptions,
            ]}
            selectedOptions={selectAllOffices ? officeOptions : selectedOffices}
            onChange={onOfficeChange}
            placeholder={t('select_offices')}
            countLabel={t('offices_selected')}
            error={saveErrors?.office}
            errorMessage={saveErrors?.office}
            disabled={!selectedPublicZone}
            direction="up"
          />
        </InputWithLabelSection>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '10px 0 0 0',
            marginTop: '100px',
            gap: '12px',
          }}
        >
          <WhiteButton onClick={handleToggle}>{t('cancel')}</WhiteButton>
          <PrimaryButton onClick={handleSubmit}>
            {isLoading ? (
              <ButtonTextLoader
                loadingText={isEditMode ? t('updating') : t('adding')}
                fontSize="13px"
              />
            ) : isEditMode ? (
              t('update')
            ) : (
              t('add')
            )}
          </PrimaryButton>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddEmtithalMasterTaskModal;
